import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Btn, Card, FilterMultiSelect, FilterSelect, OSBadges, Pagination, ProjectThumb, stageLabel, useToast } from '@/components/ui';
import type { Stage } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useProjects } from '@/hooks';
import type { ProjectSortDir, ProjectSortKey } from '@/api/projects';
import { getErrorMessage } from '@/api';
import { refreshAppsCache, runAdminFollowups } from '@/api/admin';
import { waitForSyncJob } from '@/api/sync';
import { useMetaContext } from '@/components/Layout';
import { emailLocal, projectLabel } from '@/lib/format';
import { DEFAULT_STAGE_FILTER, STAGE_FILTER_OPTIONS } from '@/lib/stageFilter';
import type { StageFilterValue } from '@/lib/stageFilter';

import styles from './ProjectList.module.css';

// Stage·검색·페이지네이션은 서버에서 함께 처리해 전체 건수와 페이지를 일치시킨다.
const UA_OPTIONS = [
  { value: '전체', label: '전체' },
  { value: '운영', label: 'UA 운영' },
  { value: '중단', label: 'UA 운영 중단' },
];

// 12는 2·3·4·6열 grid 모두에서 깔끔하게 떨어진다(16은 3·5열에서 마지막 행 외톨이 발생).
// 5열 × 4행 그리드에 맞춘 페이지 크기.
const PAGE_SIZE = 20;

export default function ProjectListPage() {
  const meta = useMetaContext();
  const toast = useToast();
  const isAdmin = meta.current_user.platform_role === 'admin';
  const [refreshing, setRefreshing] = useState(false);
  const [runningFollowups, setRunningFollowups] = useState(false);

  const onRefreshCache = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refreshAppsCache();
      // 캐시 invalidate 직후 projects 목록 재fetch.
      projects.reload();
      toast.success('캐시 갱신 완료 — 최신 BigQuery 데이터로 갱신됐어요');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  };

  const onRunFollowups = async () => {
    if (runningFollowups) return;
    setRunningFollowups(true);
    try {
      const result = await runAdminFollowups();
      if (!result.app_followups_triggered && !result.partners_refresh_job_id) {
        toast.info('후속처리가 비활성화되어 실행할 작업이 없어요');
        return;
      }
      toast.info('후속처리 실행을 요청했어요');

      const job = await waitForSyncJob(result.partners_refresh_job_id);
      if (!job) {
        toast.success('앱 후속처리 실행 요청 완료');
      } else if (job.status === 'failed') {
        toast.error(job.error_message ?? 'BI 동기화 실행에 실패했어요');
      } else if (job.status === 'partial') {
        toast.info('BI 동기화가 일부 실패했어요. 실행 결과를 확인해주세요', 5000);
      } else {
        toast.success('후속처리 실행 완료');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunningFollowups(false);
    }
  };
  // 기본값은 Stage 1 제외 — 멤버별 권한의 게임 목록과 동일(lib/stageFilter).
  const [stages, setStages] = useState<StageFilterValue[]>(DEFAULT_STAGE_FILTER);
  const [ua, setUa] = useState<string>('전체');
  const [linked, setLinked] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  // orbit PageProjectList의 정렬 UX와 동치 — 기본: 생성일순 최신 우선.
  const [sortKey, setSortKey] = useState<ProjectSortKey>('created');
  const [sortDir, setSortDir] = useState<ProjectSortDir>('desc');

  const projects = useProjects({
    stage: stages,
    ua: ua as '운영' | '중단' | '전체' | '',
    linked: linked || undefined,
    q: q.trim() || undefined,
    page,
    page_size: PAGE_SIZE,
    sort_key: sortKey,
    sort_dir: sortDir,
  });

  const total = projects.data?.total ?? 0;
  const items = projects.data?.projects ?? [];

  // 헤더에 표시할 stage 카운트는 별도 endpoint가 없으므로 현재 페이지에 보이는 행 기준 대략값.
  // 정확값을 backend가 줄 때까지 화면에서는 total만 표시.
  const headerCounts = useMemo(() => {
    const map: Record<string, number> = { 전체: total };
    return map;
  }, [total]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>프로젝트 목록</h1>
          <div className={styles.subtitle}>
            총 <span className={styles.strong}>{headerCounts.전체}</span>개 프로젝트
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAdmin && (
            <>
              <Btn
                variant="ghost"
                onClick={onRunFollowups}
                disabled={runningFollowups}
                title="utils.apps 후속 파이프라인 수동 실행 (admin 전용)"
                icon={<NavIcon name="rocket" size={14} color="currentColor" />}
              >
                {runningFollowups ? '실행 중...' : '후속처리 실행'}
              </Btn>
              <Btn
                variant="ghost"
                onClick={onRefreshCache}
                disabled={refreshing}
                title="BigQuery utils.apps 캐시 강제 갱신 (admin 전용)"
              >
                {refreshing ? '갱신 중...' : '캐시 갱신'}
              </Btn>
            </>
          )}
          <Link to="/projects/new" style={{ textDecoration: 'none' }}>
            <Btn variant="primary" icon={<NavIcon name="plus" size={14} color="currentColor" />}>
              새 프로젝트 요청
            </Btn>
          </Link>
        </div>
      </div>

      <div className={styles.filterRow}>
        <FilterMultiSelect
          label="Stage"
          values={stages}
          onChange={(v) => {
            setStages(v as StageFilterValue[]);
            setPage(1);
          }}
          options={STAGE_FILTER_OPTIONS}
        />
        <FilterSelect
          label="UA 운영"
          value={ua}
          onChange={(v) => {
            setUa(v);
            setPage(1);
          }}
          options={UA_OPTIONS}
        />
        <button
          type="button"
          className={`${styles.toggleBtn} ${linked ? styles.active : ''}`}
          onClick={() => {
            setLinked((v) => !v);
            setPage(1);
          }}
        >
          <span className={styles.toggleBox}>
            {linked && <NavIcon name="check" size={9} color="#0B0B0B" />}
          </span>
          게임 데이터 연동
        </button>
        <div className={styles.spacer} />
        {/* 정렬 컨트롤 — 검색창 왼쪽에 배치 */}
        <div className={styles.sortWrap}>
          <span className={styles.sortLabel}>정렬</span>
          <span className={styles.sortDivider} />
          <div className={styles.sortSelectWrap}>
            <select
              className={styles.sortSelect}
              value={sortKey}
              onChange={(e) => {
                setSortKey(e.target.value as ProjectSortKey);
                setPage(1);
              }}
            >
              <option value="created">생성일순</option>
              <option value="name">이름순</option>
            </select>
            <span className={styles.sortChev} aria-hidden>
              <NavIcon name="chev-d" size={13} color="#737373" />
            </span>
          </div>
          <span className={styles.sortDividerTight} />
          <button
            type="button"
            className={styles.sortDirBtn}
            onClick={() => {
              setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
              setPage(1);
            }}
            title={
              sortKey === 'name'
                ? sortDir === 'asc'
                  ? 'ㄱ→ㅎ 오름차순'
                  : 'ㅎ→ㄱ 내림차순'
                : sortDir === 'asc'
                  ? '오래된 순'
                  : '최근 순'
            }
            aria-label="정렬 방향 토글"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0B0B0B"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: sortDir === 'asc' ? 'none' : 'scaleY(-1)',
                transition: 'transform 180ms cubic-bezier(.2,.6,.2,1)',
              }}
            >
              <path d="M12 19V5" />
              <path d="m5 12 7-7 7 7" />
            </svg>
          </button>
        </div>
        <div className={styles.searchWrap}>
          <NavIcon name="search" size={14} color="#9B9B9B" />
          <input
            className={styles.searchInput}
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="이름 · bundle · prefix · id"
          />
        </div>
      </div>

      {projects.error && (
        <Card>
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>목록을 불러오지 못했어요</div>
            <div className={styles.emptyDesc}>{getErrorMessage(projects.error)}</div>
          </div>
        </Card>
      )}

      {/* 첫 로딩(데이터 없음) — skeleton 카드 16개 */}
      {projects.loading && !projects.data && !projects.error && (
        <div className={styles.grid}>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <div key={i} className={`${styles.card} ${styles.cardSkeleton}`} aria-hidden="true">
              <div className={`${styles.thumb} ${styles.skel}`} style={{ width: 64, height: 64 }} />
              <div className={styles.cardInfo}>
                <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '60%', height: 14 }} />
                <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '40%', height: 11, marginTop: 8 }} />
                <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '50%', height: 10, marginTop: 8 }} />
              </div>
              <div className={styles.cardRight}>
                <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: 56, height: 18 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 데이터 있고 결과 0 (검색 결과 없음). 단 background 로딩 중에는 이 메시지 보이지 않음 — 직전 결과 유지 */}
      {!projects.loading && !projects.error && items.length === 0 && (
        <Card>
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>검색 결과가 없어요</div>
            <div className={styles.emptyDesc}>
              다른 키워드를 입력하거나 필터를 변경해보세요
            </div>
          </div>
        </Card>
      )}

      {/* 데이터 있음 — loading 중이어도 직전 결과 그대로 노출 (stale-while-revalidate) */}
      {items.length > 0 && (
        <>
          <div
            className={styles.grid}
            style={projects.loading ? { opacity: 0.75, transition: 'opacity 180ms ease' } : undefined}
          >
            {items.map((p) => (
              <Link
                key={p.project_id ?? p.prefix}
                to={p.project_id ? `/projects/${p.project_id}` : '#'}
                className={styles.card}
                onClick={(e) => {
                  if (!p.project_id) e.preventDefault();
                }}
                style={!p.project_id ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                title={!p.project_id ? '게임 데이터 미연동' : undefined}
              >
                <ProjectThumb
                  prefix={p.prefix}
                  display_name={p.display_name}
                  iconUrl={p.icon_url ?? null}
                  size={64}
                  className={styles.thumb}
                />
                <div className={styles.cardInfo}>
                  <div className={styles.cardTitle}>
                    {/* 이름은 한 줄을 다 쓴다. Stage 칩은 아래 메타 줄(기존 app_match_name 자리)로 내렸다. */}
                    <span className={styles.cardName} title={projectLabel(p.display_name, p.prefix)}>
                      {projectLabel(p.display_name, p.prefix)}
                    </span>
                  </div>
                  <div className={styles.cardMono}>
                    {p.gcp_project_id ?? '게임 데이터 미연동'}
                  </div>
                  <div className={styles.cardMeta}>
                    {/* Stage는 칩 대신 텍스트로 — 기존 app_match_name 자리의 메타 정보와 같은 크기·색을 쓴다. */}
                    {p.stage && <>
                      <span>{stageLabel(p.stage as Stage)}</span>
                      <span className={styles.cardMetaDot} />
                    </>}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <NavIcon name="users" size={11} color="#9B9B9B" />
                      {p.member_count}
                    </span>
                  </div>
                </div>
                <div className={styles.cardRight}>
                  <OSBadges os={p.os || []} />
                  <span className={styles.cardRightId}>{p.project_id ?? 'id 미부여'}</span>
                </div>
              </Link>
            ))}
          </div>
          <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      )}

      {/* 현재 사용자 컨텍스트 표시 (간단 푸터) */}
      <div style={{ marginTop: 24, font: '400 11.5px/1.4 "Pretendard"', color: 'var(--sc-text-muted)' }}>
        로그인: {emailLocal(meta.current_user.email)} · {meta.current_user.platform_role}
      </div>
    </div>
  );
}
